import type { ContentBlock } from '@beidou/agent-protocol';
import { isItemCard } from './cardTypes';

export type CopyFormat = 'plain' | 'markdown' | 'json';

function collectBlocks(block: ContentBlock, parts: ContentBlock[]): void {
  if (block.type === 'carousel') {
    for (const item of block.items) collectBlocks(item as ContentBlock, parts);
  } else if (block.type !== 'follow_up') {
    parts.push(block);
  }
}

export function flattenBlocks(blocks: ContentBlock[]): ContentBlock[] {
  const out: ContentBlock[] = [];
  for (const b of blocks) collectBlocks(b, out);
  return out;
}

export function blocksToPlainText(blocks: ContentBlock[]): string {
  const parts: string[] = [];
  for (const block of flattenBlocks(blocks)) {
    if (block.type === 'markdown') parts.push(block.text.trim());
    else if (isItemCard(block)) {
      parts.push(block.title.trim());
      if (block.reason?.trim()) parts.push(block.reason.trim());
    }
  }
  return parts.filter(Boolean).join('\n');
}

export function blocksToMarkdown(blocks: ContentBlock[]): string {
  const parts: string[] = [];
  for (const block of flattenBlocks(blocks)) {
    if (block.type === 'markdown') parts.push(block.text.trim());
    else if (isItemCard(block)) {
      parts.push(`- **${block.title}**${block.reason ? ` — ${block.reason}` : ''}`);
    }
  }
  return parts.filter(Boolean).join('\n\n');
}

export function blocksToJson(blocks: ContentBlock[]): string {
  const items = flattenBlocks(blocks)
    .filter(isItemCard)
    .map((b) => ({
      item_id: b.item_id,
      type: b.type.replace('_card', ''),
      title: b.title,
      reason: b.reason,
    }));
  return JSON.stringify({ items, text: blocksToPlainText(blocks) }, null, 2);
}

export async function copyText(text: string): Promise<boolean> {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    /* fallback */
  }
  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(textarea);
    return ok;
  } catch {
    return false;
  }
}

export function copyBlocks(blocks: ContentBlock[], format: CopyFormat): Promise<boolean> {
  const text =
    format === 'markdown'
      ? blocksToMarkdown(blocks)
      : format === 'json'
        ? blocksToJson(blocks)
        : blocksToPlainText(blocks);
  return copyText(text);
}
